/*!
 * 1DS JS SDK Properties plugin, 2.4.7
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* TelemetryContext.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { isString } from '@microsoft/1ds-core-js';
import { Application } from './context/Application';
import { Cloud } from './context/Cloud';
import { User } from './context/User';
import { Web } from './context/Web';
import { OperatingSystem } from './context/OperatingSystem';
import { IntWeb } from './context/IntWeb';
import { Utc } from './context/Utc';
import { Loc } from './context/Loc';
import { Device } from './context/Device';
import { Session } from './context/Session';
import { Trace } from './context/Trace';
import { AppExtensionKeys, UserExtensionKeys, WebExtensionKeys, SDKExtKeys, OSExtKeys, IntWebExtKeys, UtcExtKeys, LocExtKeys, SessionExtKeys, DeviceExtKeys, TraceExtKeys, CloudExtKeys } from './ExtensionKeys';
import { Extensions } from './Extensions';
import { Sdk } from './context/Sdk';
import { SessionManager } from './SessionManager';
// Use a local variable as the reference so it will be minified for the 25ish usages
var _isString = isString;
var TelemetryContext = /** @class */ (function () {
    function TelemetryContext(coreConfig, propertiesConfig, core) {
        this.app = new Application(propertiesConfig);
        this.cloud = new Cloud();
        this.user = new User(coreConfig, propertiesConfig, core && core.logger);
        this.os = new OperatingSystem(propertiesConfig);
        this.web = new Web(propertiesConfig);
        this.sdk = new Sdk(coreConfig);
        this.intWeb = new IntWeb(propertiesConfig);
        this.utc = new Utc(propertiesConfig);
        this.loc = new Loc();
        this.device = new Device();
        this.telemetryTrace = new Trace(propertiesConfig);
        this.sessionManager = new SessionManager(propertiesConfig, core && core.logger);
        this.session = new Session();
    }
    /**
     * Returns the current session id, if one does not already exist a new one will be attempted to be created.
     * If you have set a specific custom id via session.setId(), this function will return that value.
     */
    TelemetryContext.prototype.getSessionId = function () {
        var session = this.session;
        if (session && _isString(session.customId)) {
            return session.customId;
        }
        var sessionManager = this.sessionManager;
        sessionManager.update();
        var autoSession = sessionManager.automaticSession;
        if (autoSession) {
            var autoId = autoSession.getId();
            if (autoId && _isString(autoId)) {
                session.automaticId = autoId;
            }
        }
        return session.automaticId;
    };
    TelemetryContext.prototype.applyApplicationContext = function (event) {
        var app = this.app;
        if (_isString(app.id)) {
            event.ext[Extensions.AppExt][AppExtensionKeys.id] = app.id;
        }
        if (_isString(app.ver)) {
            event.ext[Extensions.AppExt][AppExtensionKeys.ver] = app.ver;
        }
        if (_isString(app.name)) {
            event.ext[Extensions.AppExt][AppExtensionKeys.appName] = app.name;
        }
        if (_isString(app.locale)) {
            event.ext[Extensions.AppExt][AppExtensionKeys.locale] = app.locale;
        }
        var expId = app.getExpId();
        if (_isString(expId)) {
            event.ext[Extensions.AppExt][AppExtensionKeys.expId] = expId;
        }
        if (_isString(app.env)) {
            event.ext[Extensions.AppExt][AppExtensionKeys.env] = app.env;
        }
    };
    TelemetryContext.prototype.applyUserContext = function (event) {
        var user = this.user;
        var localId = user.getLocalId();
        if (_isString(localId)) {
            event.ext[Extensions.UserExt][UserExtensionKeys.localId] = localId;
        }
        if (_isString(user.locale)) {
            event.ext[Extensions.UserExt][UserExtensionKeys.locale] = user.locale;
        }
        if (_isString(user.id)) {
            event.ext[Extensions.UserExt][UserExtensionKeys.id] = user.id;
        }
    };
    TelemetryContext.prototype.applyWebContext = function (event) {
        var web = this.web;
        if (_isString(web.domain)) {
            event.ext[Extensions.WebExt][WebExtensionKeys.domain] = web.domain;
        }
        if (_isString(web.browser)) {
            event.ext[Extensions.WebExt][WebExtensionKeys.browser] = web.browser;
        }
        if (_isString(web.browserVer)) {
            event.ext[Extensions.WebExt][WebExtensionKeys.browserVer] = web.browserVer;
        }
        if (_isString(web.screenRes)) {
            event.ext[Extensions.WebExt][WebExtensionKeys.screenRes] = web.screenRes;
        }
        event.ext[Extensions.WebExt][WebExtensionKeys.userConsent] = web.getUserConsent();
        event.ext[Extensions.WebExt][WebExtensionKeys.consentDetails] = web.getUserConsentDetails();
    };
    TelemetryContext.prototype.applyOsContext = function (event) {
        var os = this.os;
        if (_isString(os.name)) {
            event.ext[Extensions.OSExt][OSExtKeys.osName] = os.name;
        }
        if (_isString(os.ver)) {
            event.ext[Extensions.OSExt][OSExtKeys.ver] = os.ver;
        }
    };
    TelemetryContext.prototype.applySdkContext = function (event) {
        var sdk = this.sdk;
        event.ext[Extensions.SdkExt][SDKExtKeys.seq] = sdk.getSequenceId();
        event.ext[Extensions.SdkExt][SDKExtKeys.epoch] = sdk.epoch;
        if (_isString(sdk.installId)) {
            event.ext[Extensions.SdkExt][SDKExtKeys.installId] = sdk.installId;
        }
    };
    TelemetryContext.prototype.applyIntWebContext = function (event) {
        var intWeb = this.intWeb;
        var msfpc = intWeb.getMsfpc();
        if (_isString(msfpc)) {
            event.ext[Extensions.IntWebExt][IntWebExtKeys.msfpc] = msfpc;
        }
        var anid = intWeb.getAnid();
        if (_isString(anid)) {
            event.ext[Extensions.IntWebExt][IntWebExtKeys.anid] = anid;
        }
        if (_isString(intWeb.serviceName)) {
            event.ext[Extensions.IntWebExt][IntWebExtKeys.serviceName] = intWeb.serviceName;
        }
    };
    TelemetryContext.prototype.applyUtcContext = function (event) {
        var utc = this.utc;
        event.ext[Extensions.UtcExt][UtcExtKeys.popSample] = utc.popSample;
        if (utc.eventFlags > 0) {
            event.ext[Extensions.UtcExt][UtcExtKeys.eventFlags] = utc.eventFlags;
        }
    };
    TelemetryContext.prototype.applyLocContext = function (event) {
        event.ext[Extensions.LocExt][LocExtKeys.tz] = this.loc.tz;
    };
    TelemetryContext.prototype.applySessionContext = function (event) {
        event.ext[Extensions.AppExt][SessionExtKeys.sessionId] = this.getSessionId();
    };
    TelemetryContext.prototype.applyDeviceContext = function (event) {
        var device = this.device;
        if (_isString(device.localId)) {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.localId] = device.localId;
        }
        if (_isString(device.make)) {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.make] = device.make;
        }
        if (_isString(device.model)) {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.model] = device.model;
        }
        if (_isString(device.deviceClass)) {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.deviceClass] = device.deviceClass;
        }
    };
    TelemetryContext.prototype.applyCloudContext = function (event) {
        var cloud = this.cloud;
        if (_isString(cloud.role)) {
            event.ext[Extensions.CloudExt][CloudExtKeys.role] = cloud.role;
        }
        if (_isString(cloud.roleInstance)) {
            event.ext[Extensions.CloudExt][CloudExtKeys.roleInstance] = cloud.roleInstance;
        }
        if (_isString(cloud.roleVer)) {
            event.ext[Extensions.CloudExt][CloudExtKeys.roleVer] = cloud.roleVer;
        }
    };
    TelemetryContext.prototype.applyAITraceContext = function (event) {
        var trace = this.telemetryTrace;
        if (_isString(trace.traceId)) {
            event.ext[Extensions.TraceExt][TraceExtKeys.traceId] = trace.traceId;
        }
        if (_isString(trace.name)) {
            event.ext[Extensions.TraceExt][TraceExtKeys.traceName] = trace.name;
        }
        if (_isString(trace.parentId)) {
            event.ext[Extensions.TraceExt][TraceExtKeys.parentId] = trace.parentId;
        }
    };
    return TelemetryContext;
}());
export { TelemetryContext };
//# sourceMappingURL=TelemetryContext.js.map